import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, computed, effect, inject, model, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { DEFAULT_DECIMAL_PRECISION, resolveConvertedUnitQuantity, xCalculateCeilPlaces, xCalculateRoundPlaces, xDecimalPlacesAsFractionDigits, xDivide, xMultiply, xSubtract } from '@phoenix/common';
import { DataResult, ICRUDOptions, simpleDeepClone } from '@phx-erp/shared';
import { AutoFocus } from 'primeng/autofocus';
import { ButtonModule } from 'primeng/button';
import { SelectLazyLoadEvent } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { lastValueFrom } from 'rxjs';
import { IPHXEditComponent } from '../../../../@core/interfaces/IPHXEditComponent.interface';
import { UiLocaleService } from '../../../../@core/services/i18n/ui-locale.service';
import { BaseListService } from '../../../../@core/services/base-list.service';
import { createSettingsSignal } from '../../../../@core/services/settings.service';
import { DocumentDirection, DocumentLineItem, IBaseModel, ProductUnitConversionLink, SearchInput, SettingType, Unit } from '../../../../generated/generated-types';
import { GenericBaseModelDropDown } from '../../../../shared/components/genericBaseModelDropDown';
import { PhxCheckboxComponent } from '../../../../shared/components/phx-input-checkbox/phx-input-checkbox.component';
import { EventExt, KeyboardEventExt, PhxInputNumberComponent } from '../../../../shared/components/phx-input-number/phx-input-number.component';
import { ProductUnitConversionLinkService } from '../../../../shared/services/product-unit-conversion-link.service';
import { UnitService } from '../../../../shared/services/unit.service';

export interface ConversionResult extends IBaseModel {
  id: string;
  unit: Partial<Unit>;
  /** Unrounded value passed to the backend. Active DLI unit keeps the chosen quantity. */
  quantity: number;
  /** Rounded for the input only: unit.decimalPlaces, else line-item decimalPlaces. */
  displayQuantity?: number;
  notRoundedQuantity: number;
  conversionRate: number;
  sourceUnit?: Partial<Unit>;
  missingQuantityForNextFullUnit: number;
  allowFractions: boolean;
  scanId?: string;
}

// Minimal service for GenericBaseModelDropDown (we use offline mode with items set directly)
class ConversionResultService extends BaseListService<ConversionResult> {
  constructor() {
    super();
  }
}

@Component({
  selector: 'app-dli-unit-conversion-drop-down',
  templateUrl: './dli-unit-conversion-drop-down.component.html',
  styleUrls: ['./dli-unit-conversion-drop-down.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, PhxInputNumberComponent, PhxCheckboxComponent, AutoFocus, ButtonModule, TooltipModule, TranslatePipe],
})
export class DliUnitConversionDropDownComponent extends GenericBaseModelDropDown<ConversionResult> implements IPHXEditComponent, AfterViewInit {

  // Component-specific inputs
  documentLineItem = model<DocumentLineItem>();
  origProductId = model<string>();
  origAddressId = model<string>();
  origUnitId = model<string>();
  origQuantity = model<number>();
  baseUnitIdentifier = model<string>();
  documentDirection = model<DocumentDirection>();
  skipLoadUnitConversions = model<boolean>(false);
  focusedIndex = model<number>();
  minFractionDigits = model<number>();
  noUnitInProduct = model<boolean>(false);
  forceBaseUnitSettingSig = createSettingsSignal(SettingType.S_DLI_UNIT_CONVERSION_FORCE_BASE_UNIT);

  onCheckIsClicked = model<boolean>(false);
  noFractionsTooltip = signal<string>(null);

  activeUnitConversionsWithSourceUnit = computed(() => {
    return this.items().some(item => item.sourceUnit !== null);
  });

  allUnitsAsConversions = model<ConversionResult[]>([]);

  private uiLocaleService = inject(UiLocaleService);
  private productUnitConversionLinkService = inject(ProductUnitConversionLinkService);
  private unitService = inject(UnitService);

  productUnitConversionLinks = model<ProductUnitConversionLink[]>(null);
  override offlineMode = true;

  // Track original quantities when editing starts to detect if quantity actually changed
  private originalQuantities = new Map<string, number>();

  // Track if we're in "all units" mode vs "conversions" mode for lazy loading
  private isAllUnitsMode = signal(false);
  // Store total count of units for lazy loading
  private unitsTotalCount = signal<number>(0);

  // Track text selection to prevent item click when user is selecting text
  private isSelectingText = false;
  private mouseDownInInput = false;
  private mouseDownPosition: { x: number; y: number } | null = null;
  private currentDraggingItem: ConversionResult | null = null;

  private inputDebounceTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly inputDebounceMs = 300;

  override searchable = model<boolean>(false);

  dliInSourceUnit = computed(() => {
    const data = this.documentLineItem();
    let conversion = this.items().find(item => (item as ConversionResult).sourceUnit !== null) as ConversionResult;
    return data?.unit?.id === conversion?.sourceUnit?.id;
  });

  constructor(
  ) {
    super(new ConversionResultService(), "ConversionResult");

    this.destroyRef.onDestroy(() => {
      this.inputDebounceTimers.forEach(timer => clearTimeout(timer));
      this.inputDebounceTimers.clear();
    });
    // Note: disabled state is managed by Formly's formControl or ControlValueAccessor's setDisabledState
    // Don't hardcode it here to allow Formly to control it
    this.placeholder.set('');
    this.useIdentifierDescriptionTemplate.set(false);
    this.optionLabelSig.set('displayText'); // Use displayText for the option label

    effect(() => {
      const selectedItem = this.documentLineItem();
      const items = this.items();
      untracked(() => {
        let unitId = selectedItem.unit?.id ?? selectedItem.unitId;
        if (unitId && !this.focusedIndex() && items.length > 0) {
          this.focusedIndex.set(this.items().findIndex(item => item.unit?.id === unitId));
        }
      })
    });

    // Watch for changes to ngModel and documentDirection
    effect(() => {
      const selectedItem = this.documentLineItem();
      const direction = this.documentDirection();
      if (selectedItem && direction) {
        untracked(() => {
          let unitInProduct = selectedItem.product?.unitId ?? selectedItem.product?.unit?.id;
          this.noUnitInProduct.set(!unitInProduct);
          //for now we only load either allUnits or conversions - maybe in future we do both
          const shouldLoad = this.allUnitsAsConversions()?.length === 0 || (this.origProductId() !== selectedItem.productId);

          if (shouldLoad) {
            if (this.noUnitInProduct()) {
              this.loadAllUnits(); // Fire and forget - async method handles its own errors
            }
            else {
              if (selectedItem.productId !== this.origProductId() || selectedItem.document?.addressId !== this.origAddressId() || selectedItem.unit?.id !== this.origUnitId()) {
                // Cache units before switching to conversions mode
                if (this.isAllUnitsMode()) {
                  this.allUnitsAsConversions.set(this.items() as ConversionResult[]);
                }
                this.isAllUnitsMode.set(false); // Switch to conversions mode
                this.offlineMode = true; // Back to offline mode for conversions
                this.loadUnitConversions(); // Fire and forget - async method handles its own errors
                // Note: allItems will be set in calculateAndSetConversions() after conversions are calculated
              }
              // if (this.origQuantity() && selectedItem.quantity !== this.origQuantity())
              //   this.calculateAndSetConversions(selectedItem);
            }
            this.origProductId.set(selectedItem.productId);
            this.origAddressId.set(selectedItem.document?.addressId);
            this.origUnitId.set(selectedItem.unit?.id);
            this.origQuantity.set(selectedItem.quantity);
          }
          // If items are empty but we have cached data, restore them
          else if ((!this.items() || this.items().length === 0)) {
            if (this.isAllUnitsMode()) {
              const cachedUnits = this.allUnitsAsConversions();
              if (cachedUnits && cachedUnits.length > 0) {
                this.items.set(cachedUnits);
                this.phxSelectComponent.items.set(cachedUnits);
              }
            } else {
              const cachedConversions = this.phxSelectComponent.allItems() as ConversionResult[];
              if (cachedConversions && cachedConversions.length > 0) {
                this.items.set(cachedConversions);
                this.phxSelectComponent.items.set(cachedConversions);
              }
            }
          }
        });
      }
    });

    effect(() => {
      let noUnitInProduct = this.noUnitInProduct();
      untracked(() => {
        if (noUnitInProduct) {
          this.searchable.set(true);
        } else {
          this.searchable.set(false);
        }
      });
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.noFractionsTooltip.set(this.i18n.translate(_('unit-conversion.no-fractions-tooltip')));
    this.minFractionDigits.set(this.getFractionDigitsForUnit(this.documentLineItem()?.unit));
  }

  /**
   * Resolves quantity decimal precision for a unit, falling back to the line item / document default.
   */
  private getQuantityDecimalPlaces(unit?: Partial<Unit> | null): number {
    let decimalPlaces = unit?.decimalPlaces ?? this.documentLineItem()?.decimalPlaces;
    if (!decimalPlaces || decimalPlaces === 0) {
      decimalPlaces = DEFAULT_DECIMAL_PRECISION;
    }
    return decimalPlaces;
  }

  getFractionDigitsForUnit(unit?: Partial<Unit> | null): number {
    return xDecimalPlacesAsFractionDigits(this.getQuantityDecimalPlaces(unit));
  }

  getFractionDigitsForItem(item: ConversionResult): number {
    return this.getFractionDigitsForUnit(item.unit);
  }

  /** Conversion rate in the active UI locale (2,8727 instead of 2.8727). */
  formatConversionRate(rate: number): string {
    return this.uiLocaleService.formatDecimal(rate);
  }

  /**
   * Displayed quantity of this unit converted into the base unit, e.g. "8 Pack = 22,9816 m2".
   * Shown when that base quantity differs from the current base-unit amount.
   */
  baseUnitMismatchLabel(item: ConversionResult): string | null {
    const exactBase = this.exactBaseQuantityForDisplayed(item);
    const baseItem = this.baseUnitItem();
    if (exactBase == null || !baseItem || item.unit?.id === baseItem.unit?.id) {
      return null;
    }
    const currentBase = this.shownQuantity(baseItem);
    if (currentBase != null && Math.abs(exactBase - currentBase) < 1e-6) {
      return null;
    }
    const displayed = this.shownQuantity(item);
    const unitText = this.uiLocaleService.formatDecimal(displayed);
    const baseText = this.uiLocaleService.formatDecimal(exactBase);
    const baseIdentifier = item.sourceUnit?.identifier ?? baseItem.unit?.identifier ?? '';
    return `${unitText} ${item.unit?.identifier ?? ''} = ${baseText} ${baseIdentifier}`.trim();
  }

  /** Value as shown in the input, rounded to the unit's decimal places. */
  private shownQuantity(item: ConversionResult): number | null {
    const raw = item.displayQuantity ?? item.quantity;
    if (raw == null) {
      return null;
    }
    return this.roundQuantity(raw, item.unit) ?? raw;
  }

  /** Displayed row quantity times its rate, in the base unit. 8 Pack * 2,8727 = 22,9816 m2. */
  private exactBaseQuantityForDisplayed(item: ConversionResult): number | null {
    const displayed = this.shownQuantity(item);
    if (displayed == null || !item.conversionRate || !item.sourceUnit) {
      return null;
    }
    return xMultiply(displayed, item.conversionRate);
  }

  private baseUnitItem(): ConversionResult | undefined {
    const lineItem = this.documentLineItem();
    const baseUnitId = lineItem?.baseUnit?.id ?? lineItem?.baseUnitId;
    if (!baseUnitId) {
      return undefined;
    }
    return (this.items() as ConversionResult[]).find(row => row.unit?.id === baseUnitId);
  }

  /**
   * Shown when the input is rounded. Example: "gerundet von 1,33333...".
   */
  roundedQuantityTooltip(item: ConversionResult): string | null {
    const quantity = item.quantity;
    const displayQuantity = item.displayQuantity;
    if (quantity == null || displayQuantity == null || Math.abs(quantity - displayQuantity) < 1e-9) {
      return null;
    }

    const rawText = this.formatTruncatedRawQuantity(quantity);
    return this.i18n.translate(_('unit-conversion.rounded-from-tooltip'), { quantity: rawText });
  }

  /** First extra decimals of the raw quantity, with "..." when more remain. */
  private formatTruncatedRawQuantity(quantity: number): string {
    const fractionDigits = 5;
    const step = 10 ** -fractionDigits;
    const shown = xCalculateRoundPlaces(quantity, step);
    const text = this.uiLocaleService.formatDecimal(shown, step);
    return Math.abs(quantity - shown) > 1e-9 ? `${text}...` : text;
  }

  /** Display rounding only. Does not change the quantity sent to the backend. */
  private applyDisplayQuantity(item: ConversionResult): void {
    item.displayQuantity = this.roundQuantity(item.quantity, item.unit) ?? item.quantity;
  }

  /**
   * Rounds a quantity up to the unit's decimal precision. Never rounds down.
   * Returns null when quantity is null/undefined.
   */
  private roundQuantity(quantity: number | null | undefined, unit?: Partial<Unit> | null): number | null {
    if (quantity == null) {
      return null;
    }
    return xCalculateCeilPlaces(quantity, this.getQuantityDecimalPlaces(unit));
  }

  /**
   * Converts a base quantity into a target unit quantity using the active conversion rate.
   */
  private calculateTargetQuantity(
    baseQuantity: number | null,
    rate: number,
    conversionRate: number,
  ): number | null {
    if (baseQuantity == null) {
      return null;
    }
    if (!rate) {
      return xDivide(baseQuantity, conversionRate);
    }
    if (rate !== conversionRate) {
      return xMultiply(baseQuantity, xDivide(rate, conversionRate));
    }
    return baseQuantity;
  }

  /**
   * Whole units ceil. Fractional units keep the real quotient (same as backend resolveConvertedUnitQuantity).
   */
  private resolveConversionQuantity(
    targetQuantity: number | null,
    allowFractions: boolean,
  ): { quantity: number | null; notRoundedQuantity: number | null; remainder: number } {
    const notRoundedQuantity = targetQuantity;
    if (targetQuantity == null) {
      return { quantity: null, notRoundedQuantity, remainder: 0 };
    }

    const quantity = resolveConvertedUnitQuantity(targetQuantity, allowFractions);
    const remainder = allowFractions ? 0 : xSubtract(targetQuantity, quantity);
    return { quantity, notRoundedQuantity, remainder };
  }

  ngAfterViewInit(): void {
    // GenericBaseModelDropDown's ngAfterViewInit handles standalone mode by creating a dummy field
    super.ngAfterViewInit();

    // Configure PhxSelect for our use case
    if (this.phxSelectComponent) {
      this.phxSelectComponent.optionLabel.set('displayText');
      this.phxSelectComponent.placeholder.set('');
      this.phxSelectComponent.useIdentifierDescriptionTemplate.set(false);

      // Set templates if they exist
      if (this.itemTemplate()) {
        this.phxSelectComponent.itemTemplate.set(this.itemTemplate());
      }
      if (this.selectedItemTemplate()) {
        this.phxSelectComponent.selectedItemTemplate.set(this.selectedItemTemplate());
      }

      // Restore items when dropdown opens if they're empty but we have cached items
      // Subscribe BEFORE parent's onShow to prevent duplicate loads
      this.phxSelectComponent.onShow.subscribe(() => {
        // Only restore if items are actually empty (not just loading)
        // Check if we already have items loaded to avoid duplicate loads
        const currentItems = this.items();
        if (currentItems && currentItems.length > 0) {
          return; // Items already loaded, don't restore
        }

        // If in all units mode and items are empty but we have cached units, restore them
        if (this.isAllUnitsMode()) {
          const cachedUnits = this.allUnitsAsConversions();
          if (cachedUnits && cachedUnits.length > 0) {
            this.items.set(cachedUnits);
            this.phxSelectComponent.items.set(cachedUnits);
            // Restore itemsCountAll for lazy loading to work
            const totalCount = this.unitsTotalCount();
            if (totalCount > 0) {
              this.phxSelectComponent.itemsCountAll.set(totalCount);
              // Reset skip to match the number of cached items (since we're restoring what was loaded)
              this.si.skip = cachedUnits.length;
              // Set isOnLastPage based on whether we have all items cached
              // If cached items length equals total count, we're on last page
              if (cachedUnits.length >= totalCount) {
                this.isOnLastPage = true;
              } else {
                this.isOnLastPage = false;
              }
              // Mark initial fetch as executed to prevent parent from loading again
              this.initialFetchExecuted = true;
            }
          }
        }
        // If in conversions mode and items are empty but we have cached conversions, restore them
        else if (!this.isAllUnitsMode()) {
          const cachedConversions = this.phxSelectComponent.allItems() as ConversionResult[];
          if (cachedConversions && cachedConversions.length > 0) {
            this.items.set(cachedConversions);
            this.phxSelectComponent.items.set(cachedConversions);
            // Mark initial fetch as executed to prevent parent from loading again
            this.initialFetchExecuted = true;
          }
        }
      });
    }
  }

  /**
   * Override executeSearch to load from UnitService and transform Unit -> ConversionResult
   * when in "all units" mode. This allows us to reuse GenericBaseModelDropDown's lazy loading.
   */
  override async executeSearch(searchInput: SearchInput, options?: ICRUDOptions): Promise<DataResult<ConversionResult>> {
    // If not in all units mode, fall back to parent (which won't be called in offline mode anyway)
    if (!this.isAllUnitsMode()) {
      return super.executeSearch(searchInput, options);
    }

    // Ensure offlineMode is false for lazy loading to work
    this.offlineMode = false;

    // Load units from UnitService
    if (!options) {
      options = { fetchPolicy: 'network-only' };
    } else {
      options.fetchPolicy = 'network-only';
    }

    // Ensure searchInput has proper pagination values
    const searchInputWithDefaults: SearchInput = {
      ...searchInput,
      skip: searchInput.skip ?? 0,
      take: searchInput.take ?? 25,
      requireTotalCount: searchInput.requireTotalCount ?? true
    };

    const result = await lastValueFrom(
      this.unitService.getItems(searchInputWithDefaults).single$.pipe(takeUntilDestroyed(this.destroyRef)),
      { defaultValue: null }
    );

    if (!result) {
      return { result: [], count: 0, isLastPage: true };
    }

    const units = result?.getUnits?.items ?? [];
    const totalItems = result?.getUnits?.totalItems ?? 0;
    const skip = searchInputWithDefaults.skip ?? 0;
    const take = searchInputWithDefaults.take ?? 25;
    // Calculate isLastPage: if skip + take >= totalItems, we're on the last page
    const isLastPage = totalItems > 0 && (skip + take >= totalItems);

    // Store total count for lazy loading (only on initial load)
    if (skip === 0) {
      this.unitsTotalCount.set(totalItems);
    }

    // Transform Unit -> ConversionResult
    const conversionResults: ConversionResult[] = units.map(unit => ({
      id: unit.id,
      unit: unit,
      sourceUnit: null,
      quantity: this.documentLineItem()?.quantity ?? 1,
      conversionRate: 1,
      missingQuantityForNextFullUnit: 0,
      allowFractions: true,
    } as ConversionResult));

    return {
      result: conversionResults,
      count: totalItems,
      isLastPage: isLastPage
    };
  }

  /**
   * Override onLazyLoad to ensure lazy loading works in all units mode.
   * In all units mode, we use the parent's lazy loading mechanism.
   */
  override async onLazyLoad(x: SelectLazyLoadEvent): Promise<void> {
    // If in all units mode, ensure offlineMode is false and use parent's lazy loading
    if (this.isAllUnitsMode()) {
      this.offlineMode = false; // Ensure offlineMode is false for lazy loading
      return super.onLazyLoad(x);
    }

    // Otherwise, fall back to parent (which won't work in offline mode anyway)
    return super.onLazyLoad(x);
  }

  /**
   * Override onFilter to perform local filtering by unit identifier and description.
   * Since we use offline mode, we filter from phxSelectComponent.allItems locally.
   */
  override async onFilter(filterText: string): Promise<void> {
    try {
      // If in all units mode, use server-side filtering via parent's onFilter
      if (this.isAllUnitsMode()) {
        // Use parent's onFilter which will call executeSearch with the filter term
        await super.onFilter(filterText);
        return;
      }

      // Otherwise, use local filtering for conversions
      const allItems = this.phxSelectComponent?.allItems() as ConversionResult[] ?? [];

      if (!filterText || filterText.trim().length === 0) {
        // No filter - show all items
        this.items.set(allItems);
        return;
      }

      const searchTerm = filterText.toLowerCase().trim();
      const filteredItems = allItems.filter(item => {
        const identifier = item.unit?.identifier?.toLowerCase() ?? '';
        const description = item.unit?.description?.toLowerCase() ?? '';
        return identifier.includes(searchTerm) || description.includes(searchTerm);
      });

      this.items.set(filteredItems);
    } finally {
      // Ensure loading is always cleared, regardless of which path was taken
      this.setLoading(false);
    }
  }

  async loadUnitConversions(): Promise<void> {
    if (this.ongoingRefresh() || this.skipLoadUnitConversions())
      return;

    const selectedItem = this.documentLineItem();
    const direction = this.documentDirection();

    if (!selectedItem || !direction || !selectedItem.productId) {
      this.productUnitConversionLinks.set(null);
      this.items.set([]);
      return;
    }

    this.setLoading(true);

    try {
      let addressId: string | undefined;

      // For IN direction, get addressId from document
      if (direction === DocumentDirection.IN && selectedItem.document?.addressId) {
        addressId = selectedItem.document.addressId;
      }

      // Fetch unit conversion config
      const result = await lastValueFrom(
        this.productUnitConversionLinkService.getItems({
          sourceUnitId: selectedItem.baseUnitId ?? selectedItem.baseUnit?.id,
          productId: selectedItem.productId,
          vendorPriceListAddressLinkId: null,
          variantParentId: selectedItem.product?.variantParentId,
          addressId: direction === DocumentDirection.IN ? addressId : null
        }).single$.pipe(takeUntilDestroyed(this.destroyRef)),
        { defaultValue: null }
      );

      // Add early return if component was destroyed (result will be null)
      if (!result) return;

      const configLinks = result.getProductUnitConversionLinks?.items ?? [] as ProductUnitConversionLink[];

      this.productUnitConversionLinks.set(simpleDeepClone(configLinks) as ProductUnitConversionLink[]);

      if (selectedItem.quantity != null && selectedItem.unit?.id) {
        this.calculateAndSetConversions(selectedItem);
      } else {
        this.items.set([]);
        this.value.set(null);
      }
    } catch (err: any) {
      console.error('Error loading unit conversions:', err);
      this.productUnitConversionLinks.set(null);
      this.items.set([]);
      this.value.set(null);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Pure calculation function that computes all conversion results based on a base quantity and unit.
   * This is used for initial setup and recalculation.
   * @param baseQuantity - The quantity to convert from
   * @param baseUnitId - The unit ID to convert from
   * @param baseRate - Optional: The specific conversion rate to use (if not provided, will be looked up)
   */
  private calculateConversions(baseQuantity: number | null, baseUnitId: string | null | undefined, baseRate?: number): ConversionResult[] {
    const links = this.productUnitConversionLinks() ?? [];
    const results: ConversionResult[] = [];
    let baseUnit: Unit | null = null;

    // Find the rate for the base unit (use provided rate if available, otherwise look it up)
    const rate = baseRate !== undefined
      ? baseRate
      : (links.find(link => link.unitConversion?.targetUnitId === baseUnitId)?.unitConversion?.rate ?? 1);

    // Calculate conversions to other units
    for (const link of links) {
      if (!link.unitConversion || link.inactive) continue;

      const conversion = link.unitConversion;

      if (!baseUnit) {
        baseUnit = conversion.sourceUnit;
      }

      if (!conversion.rate || conversion.rate === 0) continue;
      if (!conversion.targetUnit) continue;

      const targetQuantity = this.calculateTargetQuantity(baseQuantity, rate, conversion.rate);
      const { quantity, notRoundedQuantity: notRoundedTargetQuantity, remainder } = this.resolveConversionQuantity(
        targetQuantity,
        conversion.allowFractions,
      );

      const targetResult: ConversionResult = {
        id: link.id || `${conversion.id}-${conversion.targetUnitId}-${link.scanId}`,
        unit: conversion.targetUnit,
        quantity: quantity,
        notRoundedQuantity: notRoundedTargetQuantity,
        conversionRate: conversion.rate,
        scanId: link.scanId,
        missingQuantityForNextFullUnit: remainder,
        allowFractions: conversion.allowFractions,
        sourceUnit: conversion.sourceUnit
      } as ConversionResult;
      this.applyDisplayQuantity(targetResult);
      results.push(targetResult);
    }

    if (!baseUnit) {
      baseUnit = this.documentLineItem()?.unit;
    }

    // Add source unit result
    if (baseUnit) {
      const notRoundedTargetSourceQuantity = baseQuantity == null ? null : xMultiply(baseQuantity, rate ?? 1);
      const targetSourceQuantity = notRoundedTargetSourceQuantity == null
        ? null
        : resolveConvertedUnitQuantity(notRoundedTargetSourceQuantity, true);

      let sourceUnitResult: ConversionResult = {
        id: `source-${baseUnit.id}`, // we need an id here to differentiate between different conversions - since this is base unit it doesnt have a real conversion
        unit: baseUnit,
        quantity: targetSourceQuantity,
        notRoundedQuantity: notRoundedTargetSourceQuantity,
        conversionRate: null,
        missingQuantityForNextFullUnit: 0,
        allowFractions: true,
        sourceUnit: null
      } as ConversionResult;
      this.applyDisplayQuantity(sourceUnitResult);
      results.push(sourceUnitResult);
    }


    results.forEach(item => {
      this.originalQuantities.set(item.id, item.notRoundedQuantity);
    });

    return results;
  }

  /**
   * Initializes conversions from scratch. Used when first loading or when the unit/product changes.
   */
  calculateAndSetConversions(selectedItem: DocumentLineItem, skipSelected?: boolean) {
    const selectedQuantity = selectedItem.quantity;
    const selectedUnitId = selectedItem.unit?.id;

    if (selectedQuantity == null || !selectedUnitId) {
      this.items.set([]);
      this.value.set(null);
      this.originalQuantities.clear();
      return;
    }

    const results = this.calculateConversions(selectedQuantity, selectedUnitId);
    const activeUnit = results.find(result => result.unit?.id === selectedUnitId);
    if (activeUnit) {
      activeUnit.quantity = selectedQuantity;
      activeUnit.notRoundedQuantity = selectedQuantity;
      this.applyDisplayQuantity(activeUnit);
    }

    if (!skipSelected) {

      this.value.set(results.find(result => result.unit?.id === selectedUnitId));
      this.items.set(results);
      // Store conversions in allItems for restoration when dropdown reopens
      this.phxSelectComponent.allItems.set(results);
    }
  }

  /**
   * Recalculates conversions based on a changed item's quantity and unit.
   * Mutates items in place to avoid re-rendering and preserve focus.
   */
  recalculateConversionsFromItem(changedItem: ConversionResult, includeChangedItem: boolean = false): void {
    const currentItems = this.items();
    if (!currentItems || currentItems.length === 0) {
      return;
    }

    const links = this.productUnitConversionLinks() ?? [];

    // Find the link that corresponds to the changedItem by matching its unique id
    const matchingLink = links.find(link => {
      const linkId = link.id || `${link.unitConversion?.id}-${link.unitConversion?.targetUnitId}-${link.scanId}`;
      return linkId === changedItem.id;
    });

    // Get the base unit ID and rate from the matching link
    // For source unit items (id starts with "source-"), use the unit.id with rate 1
    let baseUnitId: string | null | undefined;
    let baseRate: number | undefined;

    if (changedItem.id?.startsWith('source-')) {
      // Source unit item - use its unit.id with rate 1 (it's the base unit)
      baseUnitId = changedItem.unit?.id;
      baseRate = 1;
    } else if (matchingLink?.unitConversion) {
      // Found matching link - use its targetUnitId and rate
      baseUnitId = matchingLink.unitConversion.targetUnitId;
      baseRate = matchingLink.unitConversion.rate;
    } else {
      // Fallback to unit.id (shouldn't happen, but handle gracefully)
      baseUnitId = changedItem.unit?.id;
    }

    // Calculate all conversions based on the changed item using the specific link's rate
    const recalculatedResults = this.calculateConversions(changedItem.quantity, baseUnitId, baseRate);

    // Mutate items in place to avoid re-render and preserve focus
    const items = currentItems as ConversionResult[];
    for (const currentItem of items) {
      if (currentItem.id === changedItem.id && !includeChangedItem) {
        continue;
      }
      const recalculatedItem = recalculatedResults.find(r =>
        r.unit?.id === currentItem.unit?.id && (r.scanId ?? null) === (currentItem.scanId ?? null)
      ) ?? recalculatedResults.find(r => r.unit?.id === currentItem.unit?.id);
      if (recalculatedItem) {
        currentItem.quantity = recalculatedItem.quantity;
        currentItem.notRoundedQuantity = recalculatedItem.notRoundedQuantity;
        currentItem.conversionRate = recalculatedItem.conversionRate;
        currentItem.missingQuantityForNextFullUnit = recalculatedItem.missingQuantityForNextFullUnit;
        this.applyDisplayQuantity(currentItem);
      }
    }

    const refreshed = [...items];
    this.items.set(refreshed);
    this.phxSelectComponent.items.set(refreshed);
    this.phxSelectComponent.allItems.set(refreshed);
    this.cdRef.detectChanges();
  }



  onQuantityChange(item: ConversionResult, quantity: number): void {
    // Store original quantity on first change if not already stored
    if (!this.originalQuantities.has(item.id)) {
      this.originalQuantities.set(item.id, item.quantity);
    }
    const displayed = this.roundQuantity(item.quantity, item.unit);
    if (quantity === displayed && quantity !== item.quantity) {
      item.displayQuantity = displayed;
      return;
    }
    item.quantity = quantity;
    item.displayQuantity = quantity;
    item.notRoundedQuantity = quantity;
    const existingTimer = this.inputDebounceTimers.get(item.id);
    if (existingTimer) {
      clearTimeout(existingTimer);
      this.inputDebounceTimers.delete(item.id);
    }
    const timer = setTimeout(() => {
      this.inputDebounceTimers.delete(item.id);
      if (!this.onCheckIsClicked()) {
        this.checkAndRecalculateIfQuantityChanged(item);
      }
    }, this.inputDebounceMs);
    this.inputDebounceTimers.set(item.id, timer);
  }

  setSelectedItemProperties(item: ConversionResult): void {
    const selectedItem = this.documentLineItem();
    selectedItem.quantity = item.displayQuantity ?? this.roundQuantity(item.quantity, item.unit) ?? item.quantity;
    selectedItem.unitId = item.unit?.id;
    selectedItem.unit = item.unit as Unit;
    selectedItem.unitIdentifier = item.unit?.identifier;
    this.documentLineItem.set(selectedItem);
  }

  isItemFocused(item: ConversionResult): boolean {
    const index = this.items().findIndex(i => i?.id === item?.id);
    return this.focusedIndex() === index;
  }

  onKeyDown(item: ConversionResult, event: KeyboardEventExt): void {
    let length = this.items().length;

    let key = event.key;

    if (key === 'Enter') {
      item.quantity = event.value;
      this.checkAndRecalculateIfQuantityChanged(item);
      this.finalizeItemSelection(item);
      return;
    }
    // if key combination shift + tab is pressed, focus the previous item
    else if (key === 'Tab' && event.shiftKey) {
      if (this.focusedIndex() - 1 < 0) {
        this.focusedIndex.set(0);
      }
      else {
        this.focusedIndex.set(this.focusedIndex() - 1);
      }
    }
    else if (key === 'Tab') {
      if (this.focusedIndex() + 1 >= length) {
        this.focusedIndex.set(length - 1);
      }
      else {
        this.focusedIndex.set(this.focusedIndex() + 1);
      }
    }
  }

  // Helper methods to prevent dragging on input from closing dropdown
  private isDraggingFromInput(item: ConversionResult): boolean {
    return this.isSelectingText || (this.mouseDownInInput && this.currentDraggingItem?.id === item.id);
  }

  private detectDragMovement(event: MouseEvent): boolean {
    if (!this.mouseDownPosition) return false;
    const deltaX = Math.abs(event.clientX - this.mouseDownPosition.x);
    const deltaY = Math.abs(event.clientY - this.mouseDownPosition.y);
    return deltaX > 3 || deltaY > 3;
  }

  private checkTextSelection(): void {
    const selection = window.getSelection();
    if (selection?.toString().length > 0) {
      this.isSelectingText = true;
    }
  }

  private resetDragState(): void {
    setTimeout(() => {
      this.mouseDownInInput = false;
      this.mouseDownPosition = null;
      this.currentDraggingItem = null;
      setTimeout(() => {
        this.isSelectingText = false;
      }, 150);
    }, 100);
  }

  onItemClick(item: ConversionResult, event: MouseEvent): void {
    if (this.isDraggingFromInput(item)) {
      event.preventDefault();
      event.stopPropagation();
      this.isSelectingText = false;
      this.mouseDownInInput = false;
      this.mouseDownPosition = null;
      this.currentDraggingItem = null;
      return;
    }
    event.stopPropagation();
    this.focusedIndex.set(this.items().findIndex(i => i?.id === item?.id));
  }

  /**
   * Writes this row's orange base-unit amount (e.g. 22,9816 m2) into the base unit
   * and recalculates the other units from it.
   */
  onApplyQuantityToOtherColumns(item: ConversionResult, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const exactBase = this.exactBaseQuantityForDisplayed(item);
    const baseItem = this.baseUnitItem();
    if (exactBase == null || !baseItem) {
      return;
    }
    baseItem.quantity = exactBase;
    baseItem.notRoundedQuantity = exactBase;
    this.applyDisplayQuantity(baseItem);
    this.skipLoadUnitConversions.set(true);
    this.recalculateConversionsFromItem(baseItem, true);
    for (const row of this.items() as ConversionResult[]) {
      this.originalQuantities.set(row.id, row.quantity);
    }
    this.syncLineItemQuantityFromCurrentUnit();
    this.skipLoadUnitConversions.set(false);
  }

  /** Keeps the line-item quantity aligned with the unit that is already selected. */
  private syncLineItemQuantityFromCurrentUnit(): void {
    const lineItem = this.documentLineItem();
    if (!lineItem) {
      return;
    }
    const unitId = lineItem.unit?.id ?? lineItem.unitId;
    const row = (this.items() as ConversionResult[]).find(entry => entry.unit?.id === unitId);
    if (!row || row.quantity == null) {
      return;
    }
    lineItem.quantity = row.quantity;
    this.documentLineItem.set(lineItem);
  }

  //the methods below are necessary for preventing dragging out of input to close dropdown
  onRowClick(item: ConversionResult, event: MouseEvent): void {
    if (this.isDraggingFromInput(item)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onInputMouseDown(item: ConversionResult, event: MouseEvent): void {
    this.mouseDownInInput = true;
    setTimeout(() => {
      this.currentDraggingItem = item;
      this.mouseDownPosition = { x: event.clientX, y: event.clientY };
    }, 100);
    this.isSelectingText = false;
    event.stopPropagation();
  }

  onInputMouseMove(event: MouseEvent): void {
    if (this.mouseDownInInput && this.detectDragMovement(event)) {
      this.isSelectingText = true;
    }
  }

  onItemMouseMove(item: ConversionResult, event: MouseEvent): void {
    if (this.mouseDownInInput && this.currentDraggingItem?.id === item.id && this.detectDragMovement(event)) {
      this.isSelectingText = true;
    }
  }

  onItemMouseUp(item: ConversionResult, event: MouseEvent): void {
    if (this.mouseDownInInput && this.currentDraggingItem?.id === item.id) {
      this.checkTextSelection();
      if (this.detectDragMovement(event)) {
        this.isSelectingText = true;
      }
      event.preventDefault();
      event.stopPropagation();
      this.resetDragState();
    }
  }

  onInputMouseUp(event: MouseEvent): void {
    this.checkTextSelection();
    if (this.detectDragMovement(event)) {
      this.isSelectingText = true;
    }
    event.stopPropagation();
    this.resetDragState();
  }

  onCheckClick(item: ConversionResult, event: EventExt): void {
    item.quantity = event.value;
    const existingTimer = this.inputDebounceTimers.get(item.id);
    if (existingTimer) {
      clearTimeout(existingTimer);
      this.inputDebounceTimers.delete(item.id);
    }
    // Close the dropdown
    this.onCheckIsClicked.set(true);
    // force recalculation
    this.originalQuantities.set(item.id, null);
    this.checkAndRecalculateIfQuantityChanged(item);
    setTimeout(() => {
      this.finalizeItemSelection(item);
      this.onCheckIsClicked.set(false);
    }, 100);
  }

  /**
   * When force-base-unit is enabled, selects the base-unit row instead of the edited row.
   */
  private resolveItemForSelection(item: ConversionResult): ConversionResult {
    if (!this.forceBaseUnitSettingSig()) {
      return item;
    }
    const baseUnitId = this.documentLineItem()?.baseUnit?.id ?? this.documentLineItem()?.baseUnitId;
    return (this.items() as ConversionResult[]).find(i => i?.unit?.id === baseUnitId) ?? item;
  }

  private finalizeItemSelection(item: ConversionResult): void {
    const usedItem = this.resolveItemForSelection(item);
    this.value.set({ ...usedItem });
    this.origQuantity.set(usedItem.quantity);
    this.hide();
    this.focus();
  }

  /**
   * Checks if the item's quantity has changed from its original value and recalculates conversions if needed.
   * This function compares the current item quantity with the tracked original quantity and performs
   * recalculation only if a change is detected.
   * @param item - The conversion result item to check and potentially recalculate from
   * @returns true if recalculation was performed, false otherwise
   */
  checkAndRecalculateIfQuantityChanged(item: ConversionResult, includeChangedItem: boolean = false): boolean {
    // Check if user actually interacted with this input (typed something)
    const originalQuantity = this.originalQuantities.get(item.id);

    // If no quantity change was tracked, user just clicked/focused without typing - do nothing
    if (originalQuantity === undefined) {
      // Clear any stale tracking
      this.originalQuantities.delete(item.id);
      return false;
    }

    this.skipLoadUnitConversions.set(true);

    // Get the current item from the items array (it may have been updated during typing)
    const currentItems = this.items() as ConversionResult[];
    const updatedItem = item;

    // Check if quantity actually changed (compare updatedItem with original)
    const quantityChanged = updatedItem.quantity !== originalQuantity;

    // Only recalculate if quantity was actually changed by typing
    if (quantityChanged) {
      // Recalculate other items based on this item's final quantity, preserving this item's value
      if (updatedItem.allowFractions === false && updatedItem.quantity != null) {
        updatedItem.quantity = Math.ceil(updatedItem.quantity);
      }
      updatedItem.quantity = this.roundQuantity(updatedItem.quantity, updatedItem.unit);
      updatedItem.displayQuantity = updatedItem.quantity;
      updatedItem.notRoundedQuantity = updatedItem.quantity;
      this.recalculateConversionsFromItem(updatedItem, includeChangedItem);
      // Update the documentLineItem to reflect the quantity change
      this.setSelectedItemProperties(updatedItem);

      this.skipLoadUnitConversions.set(false);
      return true;
    }

    // Don't delete originalQuantity - keep it for future comparisons against the initial state
    this.skipLoadUnitConversions.set(false);
    return false;
  }

  /**
   * Loads units using GenericBaseModelDropDown's lazy loading mechanism.
   * Sets up the component to use online mode and triggers the initial load.
   */
  async loadAllUnits(): Promise<void> {
    if (this.ongoingRefresh()) {
      return;
    }

    // Check if we already have cached units - if so, restore them instead of loading
    const cachedUnits = this.allUnitsAsConversions();
    if (cachedUnits && cachedUnits.length > 0 && this.isAllUnitsMode()) {
      // Already have cached units, just restore them
      this.items.set(cachedUnits);
      this.phxSelectComponent.items.set(cachedUnits);
      const totalCount = this.unitsTotalCount();
      if (totalCount > 0) {
        this.phxSelectComponent.itemsCountAll.set(totalCount);
        this.si.skip = cachedUnits.length;
        this.isOnLastPage = cachedUnits.length >= totalCount;
        this.initialFetchExecuted = true;
      }
      return;
    }

    // Set mode flag and enable online mode to use GenericBaseModelDropDown's lazy loading
    this.isAllUnitsMode.set(true);
    this.offlineMode = false;

    // Initialize search input with defaults
    this.si.skip = 0;
    this.si.take = 25; // Matches backend default
    this.si.requireTotalCount = true;
    this.si.term = undefined;

    // Use parent's refresh method which will call our overridden executeSearch
    const result = await this.refresh();

    // Cache units using the result directly (items() may not be updated synchronously)
    const units = (result as ConversionResult[]) ?? [];
    this.allUnitsAsConversions.set(units);

    // Find and set the matching value after initial load
    const currentUnitId = this.documentLineItem()?.unit?.id;
    if (currentUnitId) {
      const matchingItem = units.find(item => item?.unit?.id === currentUnitId);
      if (matchingItem) {
        this.value.set(matchingItem);
      }
      else {
        //TODO: find better solution - without this - doesnt work for products without unit 
        this.value.set(
          {
            id: 'current-unit',
            unit: this.documentLineItem()?.unit as Unit,
            quantity: this.documentLineItem()?.quantity,
            notRoundedQuantity: this.documentLineItem()?.quantity,
            conversionRate: 1,
            missingQuantityForNextFullUnit: 0,
            allowFractions: false,
            scanId: undefined
          }
        )
      }
    }
  }

}


